/**
 * Eufy 連携設定ページ
 *
 * Eufy Smart Scale との接続・同期設定を行う
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { useGoBack } from '@/hooks/useGoBack'
import { parseUtcDate } from '@/lib/date-utils'
import { PATHS } from '@/routes/paths'
import { useEufyStore } from '@/store/useEufyStore'

const formatLastSync = (iso: string | null) => {
  if (!iso) return '未同期'
  const d = parseUtcDate(iso)
  return d.toLocaleString('ja-JP', { dateStyle: 'medium', timeStyle: 'short' })
}

const EufyLoginDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) => {
  const { login, isStatusLoading } = useEufyStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const submit = async () => {
    try {
      await login({ email, password })
      toast.success('Eufyに接続しました')
      onOpenChange(false)
      setEmail('')
      setPassword('')
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'ログイン失敗'
      toast.error(msg)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eufy にログイン</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Eufy Lifeアプリで使用しているメールアドレスとパスワードを入力してください。 パスワードは保存されません。
          </p>
          <div className="space-y-2">
            <Label htmlFor="eufy-email">メールアドレス</Label>
            <Input
              id="eufy-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="eufy-password">パスワード</Label>
            <Input
              id="eufy-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isStatusLoading}>
            キャンセル
          </Button>
          <Button onClick={submit} disabled={isStatusLoading || !email || !password}>
            {isStatusLoading ? <Spinner /> : 'ログイン'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const SettingsEufyPage = () => {
  const goBack = useGoBack()
  const { status, isStatusLoading, checkStatus, selectCustomer, logout } = useEufyStore()
  const [loginOpen, setLoginOpen] = useState(false)

  useEffect(() => {
    void checkStatus()
  }, [checkStatus])

  const onLogout = async () => {
    await logout()
    toast.info('Eufyから切断しました')
    void checkStatus()
  }

  return (
    <div className="flex h-dvh flex-col bg-background">
      <Header title="Eufy 体重計" onClose={goBack} />
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <Card className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold">接続状態</h2>
              <p className="text-xs text-muted-foreground">Eufy Smart Scale から体重・体組成データを取得</p>
            </div>
            <div className="text-right">
              {isStatusLoading ? (
                <Spinner />
              ) : status?.authenticated ? (
                <span className="inline-flex px-2 py-1 rounded text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                  接続中
                </span>
              ) : (
                <span className="inline-flex px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                  未接続
                </span>
              )}
            </div>
          </div>

          {!status?.authenticated && (
            <div className="flex gap-2">
              <Button onClick={() => setLoginOpen(true)}>Eufyに接続</Button>
            </div>
          )}

          {status?.authenticated && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Label className="shrink-0">計測対象</Label>
                <Select
                  value={status.customer_id ?? ''}
                  onValueChange={(v) => void selectCustomer(v)}
                  disabled={status.customers.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="メンバーを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {status.customers.map((c) => (
                      <SelectItem key={c.customer_id} value={c.customer_id}>
                        {c.name ?? c.customer_id}
                        {c.is_default ? ' (本人)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">最終同期</p>
                  <p className="tabular-nums">{formatLastSync(status.last_sync_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">最終ステータス</p>
                  <p>{status.last_sync_status ?? '-'}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Link to={PATHS.EUFY.HOME}>
                  <Button variant="outline">グラフを見る</Button>
                </Link>
                <Button variant="ghost" onClick={onLogout}>
                  切断
                </Button>
              </div>
            </div>
          )}

          <EufyLoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
        </Card>
      </div>
    </div>
  )
}
